// Office-only quote margin (Session AAT, QUOTING DEPTH Loop 5). A rough, advisory GROSS margin per
// tier: the tier's annual PRICE minus an estimated annual DELIVERY cost (labour + the tier's
// included parts allowance). Purely for the office to sanity-check pricing; it is NEVER shown to the
// customer and NEVER stored on the quote. The customer-facing engine maths is unchanged.
//
// Cost model (transparent + documented):
//   labour cost   = labourCostPerHour x serviceHoursYr   (serviceHoursYr comes from the engine =
//                   VISIT_HOURS x visits/yr summed across the lifts, so it tracks the real cadence)
//   parts cost    = the tier's included parts allowance per lift, per year, x numLifts
//                   (Core £0, Care £250, Complete £400 -- the locked tier construction)
//   annual cost   = labour + parts
//   margin £      = tier annual price - annual cost
//   margin %      = margin £ / tier annual price
// Call-outs bundled into Complete are deliberately excluded (advisory only); the office reads this
// as an indicative labour-vs-price margin, not a full P&L.

export type TierKey = "core" | "care" | "complete";

// The tier's included parts allowance (per lift, per year), from the locked engine construction.
export const TIER_PARTS_ALLOWANCE: Record<TierKey, number> = { core: 0, care: 250, complete: 400 };

export interface TierMargin {
  tier: TierKey;
  annual: number; // the tier's annual price (ex VAT)
  labourCost: number; // estimated annual labour cost
  partsCost: number; // estimated annual parts cost (allowance x numLifts)
  costAnnual: number; // labourCost + partsCost
  marginPounds: number; // annual - costAnnual (can be negative)
  marginPercent: number; // marginPounds / annual (0 when annual is 0)
}

// Compute one tier's margin. Pure. Rounds money to whole pounds and the percent to one dp.
export function estimateTierMargin(input: {
  tier: TierKey;
  annual: number;
  serviceHoursYr: number;
  numLifts: number;
  labourCostPerHour: number;
}): TierMargin {
  const labourCost = Math.round(input.labourCostPerHour * Math.max(0, input.serviceHoursYr));
  const partsCost = TIER_PARTS_ALLOWANCE[input.tier] * Math.max(0, input.numLifts);
  const costAnnual = labourCost + partsCost;
  const marginPounds = Math.round(input.annual - costAnnual);
  const marginPercent = input.annual > 0 ? Math.round((marginPounds / input.annual) * 1000) / 10 : 0;
  return { tier: input.tier, annual: input.annual, labourCost, partsCost, costAnnual, marginPounds, marginPercent };
}

// Compute all three tiers' margins from a priced quote. `tiers` is the per-tier annual price map.
export function estimateQuoteMargins(input: {
  tiers: Record<TierKey, { annual: number }>;
  serviceHoursYr: number;
  numLifts: number;
  labourCostPerHour: number;
}): Record<TierKey, TierMargin> {
  const { serviceHoursYr, numLifts, labourCostPerHour } = input;
  return {
    core: estimateTierMargin({ tier: "core", annual: input.tiers.core.annual, serviceHoursYr, numLifts, labourCostPerHour }),
    care: estimateTierMargin({ tier: "care", annual: input.tiers.care.annual, serviceHoursYr, numLifts, labourCostPerHour }),
    complete: estimateTierMargin({ tier: "complete", annual: input.tiers.complete.annual, serviceHoursYr, numLifts, labourCostPerHour }),
  };
}
